import React, { useState, useEffect, useCallback } from 'react';
import { Search, UserPlus, UserMinus, AlertTriangle } from 'lucide-react';
import { Modal } from 'react-bootstrap';
import _ from 'lodash';

interface User {
    id: string;
    email: string;
    fullName: string;
    groups: string[];
}

interface Group {
    id: string;
    name: string;
    description?: string;
}

interface GroupMembersModalProps {
    group: Group | null;
    isOpen: boolean;
    onClose: () => void;
    onAddUser: (userId: string, groupId: string) => Promise<void>;
    onRemoveUser: (userId: string, groupId: string) => Promise<void>;
    onSearchUsers: (query: string, groupId: string, isMember: boolean) => Promise<User[]>;
    setError?: (message: string) => void;
}

const GroupMembersModal: React.FC<GroupMembersModalProps> = ({
                                                                 group,
                                                                 isOpen,
                                                                 onClose,
                                                                 onAddUser,
                                                                 onRemoveUser,
                                                                 onSearchUsers,
                                                                 setError
                                                             }) => {
    console.log("GroupMembersModal rendered with props:", {
        groupId: group?.id,
        isOpen,
        hasOnAddUser: !!onAddUser,
        hasOnRemoveUser: !!onRemoveUser,
        hasOnSearchUsers: !!onSearchUsers
    });

    const [searchTerm, setSearchTerm] = useState('');
    const [availableUsers, setAvailableUsers] = useState<User[]>([]);
    const [groupMembers, setGroupMembers] = useState<User[]>([]);
    const [selectedUser, setSelectedUser] = useState<User | null>(null);
    const [actionType, setActionType] = useState<'add' | 'remove' | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [processingUserIds, setProcessingUserIds] = useState<Set<string>>(new Set());

    // DEBUG: Log state changes
    useEffect(() => {
        console.log("State updated:", {
            availableUsers: availableUsers.map(u => ({ id: u.id, email: u.email })),
            groupMembers: groupMembers.map(u => ({ id: u.id, email: u.email })),
            selectedUser: selectedUser?.email,
            actionType,
            isLoading,
            processingUserIds: Array.from(processingUserIds)
        });
    }, [availableUsers, groupMembers, selectedUser, actionType, isLoading, processingUserIds]);

    // Function to load users with search - with detailed logging
    const loadUsers = useCallback(async (search: string = '') => {
        if (!group) {
            console.log("loadUsers: No group provided, returning early");
            return;
        }

        console.log(`loadUsers: Starting to load users with search="${search}" for group=${group.id}`);
        setIsLoading(true);

        try {
            console.log("loadUsers: Fetching group members...");
            // Step 1: Fetch current group members
            const groupMembersList = await onSearchUsers(search, group.id, true);
            console.log("loadUsers: Group members fetched:", {
                count: groupMembersList.length,
                members: groupMembersList.map(u => ({ id: u.id, email: u.email, fullName: u.fullName }))
            });

            console.log("loadUsers: Fetching all users...");
            // Step 2: Fetch all available users
            const allUsers = await onSearchUsers(search, group.id, false);
            console.log("loadUsers: All users fetched:", {
                count: allUsers.length,
                users: allUsers.map(u => ({ id: u.id, email: u.email, fullName: u.fullName }))
            });

            // Step 3: Create Set of member IDs for efficient filtering
            console.log("loadUsers: Creating member ID set for filtering");
            const memberIdSet = new Set(groupMembersList.map(member => member.id));
            console.log("loadUsers: Member ID set created:", {
                setSize: memberIdSet.size,
                memberIds: Array.from(memberIdSet)
            });

            // Step 4: Filter available users to exclude members
            console.log("loadUsers: Filtering available users to exclude members");
            const availableUsersList = allUsers.filter(user => !memberIdSet.has(user.id));
            console.log("loadUsers: Available users after filtering:", {
                beforeCount: allUsers.length,
                afterCount: availableUsersList.length,
                filteredUsers: availableUsersList.map(u => ({ id: u.id, email: u.email, fullName: u.fullName }))
            });

            // Step 5: Sort and limit results
            console.log("loadUsers: Sorting and taking top 5 results");
            const sortedMembers = _.sortBy(groupMembersList, 'fullName').slice(0, 5);
            const sortedAvailable = _.sortBy(availableUsersList, 'fullName').slice(0, 5);

            // Step 6: Update state
            console.log("loadUsers: Updating state with sorted results", {
                memberCount: sortedMembers.length,
                availableCount: sortedAvailable.length
            });
            setGroupMembers(sortedMembers);
            setAvailableUsers(sortedAvailable);

        } catch (error) {
            console.error('loadUsers: Error loading users:', error);
            if (setError) {
                setError('Failed to load users. Please try again.');
            }
        } finally {
            console.log("loadUsers: Finished - setting isLoading to false");
            setIsLoading(false);
        }
    }, [group, onSearchUsers, setError]);

    // Reset modal state
    const resetModalState = useCallback(() => {
        console.log("resetModalState: Resetting modal state");
        setSearchTerm('');
        setAvailableUsers([]);
        setGroupMembers([]);
        setSelectedUser(null);
        setActionType(null);
        setProcessingUserIds(new Set());
    }, []);

    // Handle search with debouncing
    useEffect(() => {
        console.log(`Search term changed to: "${searchTerm}"`);
        const timer = setTimeout(() => {
            console.log(`Search debounce completed, executing loadUsers with term: "${searchTerm}"`);
            loadUsers(searchTerm);
        }, 300);

        return () => {
            console.log("Clearing search debounce timer");
            clearTimeout(timer);
        };
    }, [searchTerm, loadUsers]);

    // Load initial data when modal opens
    useEffect(() => {
        console.log("Modal open state changed:", { isOpen, groupId: group?.id });
        if (isOpen && group) {
            console.log("Modal opened with group, loading initial data");
            loadUsers();
        } else if (!isOpen) {
            console.log("Modal closed, resetting state");
            resetModalState();
        }
    }, [isOpen, group, loadUsers, resetModalState]);

    // Handle user action (add/remove)
    const handleUserAction = async (user: User, action: 'add' | 'remove') => {
        console.log(`handleUserAction: ${action} user`, { userId: user.id, email: user.email });
        if (!group) {
            console.log("handleUserAction: No group, returning early");
            return;
        }

        if (processingUserIds.has(user.id)) {
            console.log("handleUserAction: User already being processed, skipping");
            return;
        }

        setSelectedUser(user);
        setActionType(action);
    };

    // Handle confirmed action
    const handleConfirmedAction = async () => {
        if (!selectedUser || !actionType || !group) {
            console.log("handleConfirmedAction: Missing required data, returning early", {
                hasSelectedUser: !!selectedUser,
                actionType,
                hasGroup: !!group
            });
            return;
        }

        console.log(`handleConfirmedAction: Confirming ${actionType} for user`, {
            userId: selectedUser.id,
            email: selectedUser.email,
            groupId: group.id
        });

        setProcessingUserIds(prev => {
            const newSet = new Set(prev).add(selectedUser.id);
            console.log("handleConfirmedAction: Added user to processing set", {
                userId: selectedUser.id,
                newProcessingCount: newSet.size
            });
            return newSet;
        });

        try {
            if (actionType === 'add') {
                console.log("handleConfirmedAction: Adding user to group");

                // Optimistically update UI
                console.log("handleConfirmedAction: Optimistically updating UI for add");
                setAvailableUsers(prev => {
                    const result = prev.filter(u => u.id !== selectedUser.id);
                    console.log("handleConfirmedAction: Updated available users", {
                        before: prev.length,
                        after: result.length
                    });
                    return result;
                });

                setGroupMembers(prev => {
                    const result = _.sortBy([...prev, selectedUser], 'fullName').slice(0, 5);
                    console.log("handleConfirmedAction: Updated group members", {
                        before: prev.length,
                        after: result.length
                    });
                    return result;
                });

                // Perform API call
                console.log("handleConfirmedAction: Calling onAddUser API");
                await onAddUser(selectedUser.id, group.id);
                console.log("handleConfirmedAction: API call successful");

            } else {
                console.log("handleConfirmedAction: Removing user from group");

                // Optimistically update UI
                console.log("handleConfirmedAction: Optimistically updating UI for remove");
                setGroupMembers(prev => {
                    const result = prev.filter(u => u.id !== selectedUser.id);
                    console.log("handleConfirmedAction: Updated group members", {
                        before: prev.length,
                        after: result.length
                    });
                    return result;
                });

                setAvailableUsers(prev => {
                    const result = _.sortBy([...prev, selectedUser], 'fullName').slice(0, 5);
                    console.log("handleConfirmedAction: Updated available users", {
                        before: prev.length,
                        after: result.length
                    });
                    return result;
                });

                // Perform API call
                console.log("handleConfirmedAction: Calling onRemoveUser API");
                await onRemoveUser(selectedUser.id, group.id);
                console.log("handleConfirmedAction: API call successful");
            }

            // Refresh lists to ensure accuracy
            console.log("handleConfirmedAction: Refreshing user lists");
            await loadUsers(searchTerm);

        } catch (error) {
            console.error('handleConfirmedAction: Error updating group membership:', error);
            if (setError) {
                setError('Failed to update group membership. Please try again.');
            }
            // Revert optimistic update
            console.log("handleConfirmedAction: Error occurred, reverting by reloading users");
            await loadUsers(searchTerm);

        } finally {
            console.log("handleConfirmedAction: Cleanup after action");
            setProcessingUserIds(prev => {
                const next = new Set(prev);
                next.delete(selectedUser.id);
                console.log("handleConfirmedAction: Removed user from processing set", {
                    userId: selectedUser.id,
                    newProcessingCount: next.size
                });
                return next;
            });

            setSelectedUser(null);
            setActionType(null);
        }
    };

    const renderUserItem = (user: User, type: 'available' | 'member') => {
        const isProcessing = processingUserIds.has(user.id);
        const isAvailable = type === 'available';

        return (
            <div
                key={user.id}
                className="list-group-item d-flex justify-content-between align-items-center p-3"
            >
                <div>
                    <div className="text-sm fw-medium">{user.fullName}</div>
                    <small className="text-muted">{user.email}</small>
                </div>
                <button
                    className={`btn btn-sm ${isAvailable ? 'btn-outline-primary' : 'btn-outline-danger'}`}
                    onClick={() => handleUserAction(user, isAvailable ? 'add' : 'remove')}
                    disabled={isProcessing}
                >
                    {isProcessing ? (
                        <span className="spinner-border spinner-border-sm" />
                    ) : isAvailable ? (
                        <UserPlus size={16} />
                    ) : (
                        <UserMinus size={16} />
                    )}
                </button>
            </div>
        );
    };

    if (!group) {
        console.log("Rendering: No group provided, returning null");
        return null;
    }

    console.log("Rendering modal with data:", {
        groupName: group.name,
        availableUsers: availableUsers.length,
        groupMembers: groupMembers.length,
        isLoading,
        hasConfirmation: !!selectedUser && !!actionType
    });

    return (
        <Modal
            show={isOpen}
            onHide={() => {
                console.log("Modal close button clicked");
                onClose();
            }}
            size="lg"
            backdrop="static"
            keyboard={false}
        >
            <div className="modal-fixed-height">
                <Modal.Header closeButton className="border-bottom">
                    <Modal.Title>
                        <div>Manage Group Members - {group.name}</div>
                        {group.description && (
                            <small className="text-muted">{group.description}</small>
                        )}
                    </Modal.Title>
                </Modal.Header>

                <Modal.Body className="p-0 overflow-hidden">
                    <div className="modal-scroll-content" style={{ height: '600px', overflowY: 'auto' }}>
                        <div className="p-4">
                            {/* Confirmation Alert */}
                            {selectedUser && actionType && (
                                <div className="alert alert-warning d-flex gap-2 align-items-start mb-3">
                                    <AlertTriangle className="mt-1" size={20} />
                                    <div className="flex-grow-1">
                                        <p className="mb-2">
                                            Are you sure you want to {actionType === 'remove' ? 'remove' : 'add'}{' '}
                                            <strong>{selectedUser.fullName}</strong>{' '}
                                            <span className="text-muted">({selectedUser.email})</span>{' '}
                                            {actionType === 'remove' ? 'from' : 'to'} this group?
                                        </p>
                                        <div className="d-flex gap-2">
                                            <button
                                                className={`btn btn-sm ${actionType === 'remove' ? 'btn-danger' : 'btn-primary'}`}
                                                onClick={handleConfirmedAction}
                                            >
                                                {actionType === 'remove' ? 'Yes, Remove' : 'Yes, Add'}
                                            </button>
                                            <button
                                                className="btn btn-sm btn-outline-secondary"
                                                onClick={() => {
                                                    console.log("Confirmation canceled");
                                                    setSelectedUser(null);
                                                    setActionType(null);
                                                }}
                                            >
                                                Cancel
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Search Input */}
                            <div className="mb-4">
                                <div className="input-group">
                                    <span className="input-group-text border">
                                        <Search size={18} />
                                    </span>
                                    <input
                                        type="text"
                                        className="form-control border"
                                        style={{height: '38px'}}
                                        placeholder="Search users..."
                                        value={searchTerm}
                                        onChange={(e) => {
                                            console.log(`Search input changed: "${e.target.value}"`);
                                            setSearchTerm(e.target.value);
                                        }}
                                    />
                                </div>
                            </div>

                            {/* User Lists */}
                            <div className="row g-4">
                                {/* Available Users */}
                                <div className="col-md-6">
                                    <h6 className="text-sm fw-medium mb-3">
                                        Available Users
                                        <span className="text-muted ms-2">(Top 5)</span>
                                    </h6>
                                    <div className="card border shadow-sm">
                                        <div className="list-group list-group-flush" style={{ minHeight: '300px' }}>
                                            {isLoading ? (
                                                <div className="text-center p-3">
                                                    <div className="spinner-border spinner-border-sm text-primary" />
                                                </div>
                                            ) : (
                                                <>
                                                    {availableUsers.map(user => renderUserItem(user, 'available'))}
                                                    {!isLoading && availableUsers.length === 0 && (
                                                        <div className="text-muted text-center p-3">
                                                            {searchTerm ? 'No matching users found' : 'No available users'}
                                                        </div>
                                                    )}
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* Current Members */}
                                <div className="col-md-6">
                                    <h6 className="text-sm fw-medium mb-3">
                                        Current Members
                                        <span className="text-muted ms-2">(Top 5)</span>
                                    </h6>
                                    <div className="card border shadow-sm">
                                        <div className="list-group list-group-flush" style={{ minHeight: '300px' }}>
                                            {isLoading ? (
                                                <div className="text-center p-3">
                                                    <div className="spinner-border spinner-border-sm text-primary" />
                                                </div>
                                            ) : (
                                                <>
                                                    {groupMembers.map(user => renderUserItem(user, 'member'))}
                                                    {!isLoading && groupMembers.length === 0 && (
                                                        <div className="text-muted text-center p-3">
                                                            {searchTerm ? 'No matching members found' : 'No members in this group'}
                                                        </div>
                                                    )}
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </Modal.Body>

                <Modal.Footer className="border-top">
                    <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() => {
                            console.log("Footer close button clicked");
                            onClose();
                        }}
                    >
                        Close
                    </button>
                </Modal.Footer>
            </div>
        </Modal>
    );
};

export default GroupMembersModal;